import { Col, Row, Typography } from 'antd';
import { AxiosResponse } from 'axios';
import _ from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SelectField, ManufacturingSizeInputField } from 'src/components/form';
import {
  FLAT_PIPE_PRODUCT,
  MACHINING_METHOD,
  REGEX,
  ROUND,
  SHAPE,
  MACHINES_TO_SORT_SIZE,
  TYPE_NAME_INPUT,
} from 'src/constants/app';
import useConvertSize from 'src/hooks/useConvertSizeMultiple';
import useMultipleManufacturingSize from 'src/queries/useMultipleManufacturingSize';
import useManufacturingNextSizesZ0 from 'src/queries/useEstimateManufacturingNextSizesZ0';
import useManufacturingNextSizes from 'src/queries/useEstimateManufacturingNextSizes';
import {
  roundSizeForMachiningMethodFromH2ToH5,
  formatSizeToFiveDecimalsIfMethodIsH2ToH5,
  getSizesWithOriginalValue,
  isDomesticProduct,
  isImportedProduct,
  removeDuplicateObject,
  getUniqueSortedItemsByConvertedValue,
  checkHasDomesticData,
  checkIsProductFinished,
  mapDataOptionsWithFractions,
  getShowProcessingRange,
  sortSizesByValue,
  getSnpoBunsuHyojiFlags,
} from 'src/utils/helper';
import { renderToleranceOptions } from 'src/utils/tolerance';
import { productFormat } from 'src/utils/product';
import { processFormatAndValues, getLabelDimension } from 'src/utils/quotation';
import { FormValue } from '../useForm';
import useTolerance from 'src/queries/useTolerance';
import Tooltip from 'antd/es/tooltip';
import useProductInformation from 'src/queries/useProductInformationMultiple';
import { NUMBER_OF_INPUT_DIMENSIONS } from '../constants';

type Props = {
  index: number;
  formInfo: UseFormReturn<FormValue, any>;
  setEstimateErrors: (e) => void;
  setStockError: (e) => void;
  dataReEstimate: Response.QuotationItem;
  fractionsData?: Response.Fractions[];
};

type TypeNameInput = typeof TYPE_NAME_INPUT[keyof typeof TYPE_NAME_INPUT];

const ManuFacturingSize: React.FC<Props> = ({
  index,
  formInfo,
  setEstimateErrors,
  setStockError,
  dataReEstimate,
  fractionsData,
}) => {
  const { control, getValues, setValue, watch, clearErrors } = formInfo;
  const { t } = useTranslation();

  const watchedMachiningItem = watch(`priceQuotes.${index}.machiningItem`);
  const productCode = watch(`priceQuotes.${index}.product`);
  const varietyCode = watch(`priceQuotes.${index}.variety`);
  const shapeCode = watch(`priceQuotes.${index}.shape`);
  const isResetByMachiningMethod = watch(
    `priceQuotes.${index}.isResetByMachiningMethod`,
  );
  const isResetByProductSelection = watch(
    `priceQuotes.${index}.isResetByProductSelection`,
  );
  const isShowMachiningTolerance = watch(
    `priceQuotes.${index}.isShowMachiningTolerance`,
  );
  const isSwapDimension = watch(`priceQuotes.${index}.isSwapDimension`);
  const inputAutoFillByType = watch(`priceQuotes.${index}.inputAutoFillByType`);

  const isProductFinished = checkIsProductFinished(varietyCode);

  const [shouldFetchManufacturingSize, setShouldFetchManufacturingSize] =
    useState(false);
  const [manufacturingSizes, setManufacturingSizes] =
    useState<Response.ManufacturingItem>();

  const [disableProductWidthInput, setDisableProductWidthInput] =
    useState(false);
  const [disableProductLengthInput, setDisableProductLengthInput] =
    useState(false);
  const [disableDimension4Input, setDisableDimension4Input] = useState(false);
  const [disableDimension5Input, setDisableDimension5Input] = useState(false);
  const [disableDimension6Input, setDisableDimension6Input] = useState(false);
  const [reEstimateWidth, setReEstimateWidth] = useState('');
  const [reEstimateLength, setReEstimateLength] = useState('');
  const [reEstimateDimension4, setReEstimateDimension4] = useState('');
  const [reEstimateDimension5, setReEstimateDimension5] = useState('');
  const [reEstimateDimension6, setReEstimateDimension6] = useState('');
  const [isBothTypes, setIsBothTypes] = useState(false);
  const [isToggleOnBlurSize1, setIsToggleOnBlurSize1] = useState(false);
  const [productWidthData, setProductWidthData] = useState<any>();
  const [productLengthData, setProductLengthData] = useState<any>();
  const [dimension4Data, setDimension4Data] = useState<any>();
  const [dimension5Data, setDimension5Data] = useState<any>();
  const [dimension6Data, setDimension6Data] = useState<any>();
  const [decimalInputSizeFlags, setDecimalInputSizeFlags] =
    useState<Payload.DecimalInputSizeFlags>();
  const [shouldFetchProductsInfor, setShouldFetchProductsInfor] =
    useState(false);
  const [labelUnitSize1, setLabelUnitSize1] = useState<string>('');

  const productWidthDataRef = useRef(productWidthData);
  const productLengthDataRef = useRef(productLengthData);
  const dimension4DataRef = useRef(dimension4Data);
  const dimension5DataRef = useRef(dimension5Data);
  const dimension6DataRef = useRef(dimension6Data);
  const machiningMethodOfZ0AndH2ToH5 = [
    MACHINING_METHOD.Z0,
    ...MACHINING_METHOD.H2TOH5,
  ];
  const [sizeInputNum, setSizeInputNum] = useState<number>(0);

  const listProductId = {
    hakudoShhnCd: productFormat.splitProductId(dataReEstimate?.product)
      ?.hakudoShhnCd,
    shhnCd: productFormat.splitProductId(dataReEstimate?.product)?.shhnCd,
    api1ShhnCd: productFormat.splitProductId(dataReEstimate?.product)
      ?.api1ShhnCd,
    api2ShhnCd: productFormat.splitProductId(dataReEstimate?.product)
      ?.api2ShhnCd,
  };

  // Conditionally pass params to control API call
  const { productsInfor: productFinishedInfor } = useProductInformation(
    listProductId,
    isProductFinished,
    shouldFetchProductsInfor,
  );

  const listOptionProductFinshed = useMemo(() => {
    const listOptions = productFinishedInfor.map((item) => ({
      label: item.seihnJoho,
      value: item.seihnJoho,
    }));

    return listOptions;
  }, [productFinishedInfor]);

  const isDomesticProductFlag = isDomesticProduct(productCode);

  const manufacturingSizeOptions = useMemo(() => {
    if (!decimalInputSizeFlags?.isSnpo1BunsuHyoji) {
      return manufacturingSizes?.sizes;
    }
    return mapDataOptionsWithFractions(
      manufacturingSizes?.sizes,
      fractionsData,
      null,
      null,
      null,
      manufacturingSizes?.snpoBunsuHyojis,
    );
  }, [decimalInputSizeFlags, manufacturingSizes?.sizes, fractionsData]);

  const productWidthDataOptions = useMemo(() => {
    if (!decimalInputSizeFlags?.isSnpo2BunsuHyoji) {
      return productWidthData;
    }
    return mapDataOptionsWithFractions(
      productWidthData,
      fractionsData,
      isDomesticProductFlag,
      watchedMachiningItem?.value,
    );
  }, [
    decimalInputSizeFlags,
    productWidthData,
    fractionsData,
    isDomesticProductFlag,
    watchedMachiningItem?.value,
  ]);

  const productLengthDataOptions = useMemo(() => {
    if (!decimalInputSizeFlags?.isSnpo3BunsuHyoji) {
      return productLengthData;
    }

    const isRoundedTo2Digits = sizeInputNum === 3;
    return mapDataOptionsWithFractions(
      productLengthData,
      fractionsData,
      isDomesticProductFlag,
      watchedMachiningItem?.value,
      isRoundedTo2Digits,
    );
  }, [
    decimalInputSizeFlags,
    productLengthData,
    fractionsData,
    isDomesticProductFlag,
    watchedMachiningItem?.value,
    sizeInputNum,
  ]);

  const dimension4DataOptions = useMemo(() => {
    if (!decimalInputSizeFlags?.isSnpo4BunsuHyoji) {
      return dimension4Data;
    }

    const isRoundedTo2Digits = sizeInputNum === 4;
    return mapDataOptionsWithFractions(
      dimension4Data,
      fractionsData,
      isDomesticProductFlag,
      watchedMachiningItem?.value,
      isRoundedTo2Digits,
    );
  }, [
    decimalInputSizeFlags,
    dimension4Data,
    fractionsData,
    isDomesticProductFlag,
    watchedMachiningItem?.value,
    sizeInputNum,
  ]);

  const dimension5DataOptions = useMemo(() => {
    if (!decimalInputSizeFlags?.isSnpo5BunsuHyoji) {
      return dimension5Data;
    }

    const isRoundedTo2Digits = sizeInputNum === 5;
    return mapDataOptionsWithFractions(
      dimension5Data,
      fractionsData,
      isDomesticProductFlag,
      watchedMachiningItem?.value,
      isRoundedTo2Digits,
    );
  }, [
    decimalInputSizeFlags,
    dimension5Data,
    fractionsData,
    isDomesticProductFlag,
    watchedMachiningItem?.value,
    sizeInputNum,
  ]);

  const dimension6DataOptions = useMemo(() => {
    if (!decimalInputSizeFlags?.isSnpo6BunsuHyoji) {
      return dimension6Data;
    }

    const isRoundedTo2Digits = sizeInputNum === 6;
    return mapDataOptionsWithFractions(
      dimension6Data,
      fractionsData,
      isDomesticProductFlag,
      watchedMachiningItem?.value,
      isRoundedTo2Digits,
    );
  }, [
    decimalInputSizeFlags,
    dimension6Data,
    fractionsData,
    isDomesticProductFlag,
    watchedMachiningItem?.value,
    sizeInputNum,
  ]);

  const prevProductRef = useRef(dataReEstimate?.product);
  const prevManuFacturingSizeRef = useRef(dataReEstimate?.setdnKakoCd);
  // Track when dataReEstimate?.product changes
  useEffect(() => {
    const currentProduct = dataReEstimate?.product;
    const prevProduct = prevProductRef.current;

    const currentManuFacturingSize = watchedMachiningItem?.value;
    const prevManuFacturingSize = prevManuFacturingSizeRef.current;

    if (
      (currentProduct !== prevProduct && currentProduct) ||
      (currentManuFacturingSize !== prevManuFacturingSize &&
        currentManuFacturingSize)
    ) {
      setShouldFetchManufacturingSize(true);
    }
    if (currentProduct !== prevProduct && currentProduct) {
      setShouldFetchProductsInfor(true);
    }

    prevProductRef.current = currentProduct;
  }, [dataReEstimate?.product, watchedMachiningItem]);

  const handleDimensionOptions = ({
    shouldDisplayFraction,
    dataRef,
    getSizesData,
    setData,
    setDisableInput,
    fieldName,
    fieldNameDisabled,
    formatFn, // function to round decimal numbers
    reEstimateValue,
  }) => {
    const machiningValue = watchedMachiningItem?.value || '';

    if (
      machiningMethodOfZ0AndH2ToH5.includes(machiningValue) ||
      shouldDisplayFraction
    ) {
      if (isBothTypes && _.size(dataRef.current) > 0) {
        const data = getSizesData
          ? removeDuplicateObject([...dataRef.current, ...getSizesData])
          : dataRef.current;

        if (
          (MACHINING_METHOD.H2TOH5.includes(machiningValue) && !getSizesData) ||
          _.size(data) > 1
        ) {
          setData(data);
          setDisableInput(false);
          setValue(fieldNameDisabled, false);
        } else {
          const value =
            formatFn(dataRef.current?.[0]?.value, machiningValue) ||
            reEstimateValue;
          const options = [{ label: value, value, originalValue: value }];
          setData(options);
          setDisableInput(!shouldDisplayFraction);
          setValue(fieldNameDisabled, true);

          if (machiningMethodOfZ0AndH2ToH5.includes(machiningValue)) {
            !shouldDisplayFraction &&
              setValue(
                fieldName,
                formatFn(dataRef.current?.[0]?.value, machiningValue) ||
                  reEstimateValue,
              );
          }
        }
      } else {
        if (_.size(getSizesData) > 1) {
          setData(getSizesData);
          setDisableInput(false);
          setValue(fieldNameDisabled, false);
        } else {
          if (_.size(getSizesData) === 0 && isBothTypes) {
            setData(undefined);
            setDisableInput(false);
            setValue(fieldNameDisabled, false);
            return;
          }
          const sizeValue = getSizesData?.[0]?.value || '';
          const value = formatFn(sizeValue, machiningValue) || reEstimateValue;
          const options = [{ label: value, value, originalValue: value }];
          setData(options);
          setDisableInput(!shouldDisplayFraction);
          setValue(fieldNameDisabled, true);

          if (machiningMethodOfZ0AndH2ToH5.includes(machiningValue)) {
            !shouldDisplayFraction &&
              setValue(
                fieldName,
                formatFn(sizeValue, machiningValue) || reEstimateValue,
              );
          }
        }
      }
    } else if (
      shapeCode === SHAPE.PIPE &&
      (!fieldName.includes('width') ? isFlatPipeProduct() : true)
    ) {
      if (isBothTypes && _.size(dataRef.current) > 0) {
        const datas = removeDuplicateObject([
          ...dataRef.current,
          ...getSizesData,
        ]);
        setData(datas);
      } else {
        setData(getSizesData);
      }
      setDisableInput(false);
      setValue(fieldNameDisabled, false);
    }
  };

  const mapToOptions = (arr?: Response.ConvertAndOriginals[]) =>
    arr?.map((item) => ({ ...item, convertedValue: item.originalValue })) ?? [];

  const handleSuccessDomestic = (
    res: AxiosResponse<Response.EstimateManufacturingNextSizesZ0Response, any>,
    args: Payload.ManufacturingSizeDomestic,
  ) => {
    if (res?.data?.item?.nextSizes) {
      // Split the sizes by comma, so each field can be identified by its index
      const sizeFields: string[] = args?.currentSizes?.split(',') ?? [];
      const sizeFieldsCount = sizeFields.length;

      const sizeOptions = mapToOptions(res?.data?.item?.nextSizes);
      //remove zero and duplicate item in array
      const uniqProductSizeData =
        getUniqueSortedItemsByConvertedValue(sizeOptions);

      const getSizesData = getSizesWithOriginalValue(
        uniqProductSizeData,
        false,
        sizeFieldsCount === sizeInputNum ? ROUND.TO_2_DIGIT : ROUND.TO_5_DIGIT,
      );

      const getSizesWidthData = sizeFieldsCount === 1 ? getSizesData : [];
      const getSizesLengthData = sizeFieldsCount === 2 ? getSizesData : [];
      const getSizesDimension4Data = sizeFieldsCount === 3 ? getSizesData : [];
      const getSizesDimension5Data = sizeFieldsCount === 4 ? getSizesData : [];
      const getSizesDimension6Data = sizeFieldsCount === 5 ? getSizesData : [];

      const hasWidthOptions = !!args?.sizes2;
      const shouldDisplayFraction = hasWidthOptions
        ? decimalInputSizeFlags?.isSnpo3BunsuHyoji
        : decimalInputSizeFlags?.isSnpo2BunsuHyoji;

      //set value and options width
      if (sizeFieldsCount === 1 && valueProductThickness) {
        handleDimensionOptions({
          shouldDisplayFraction,
          dataRef: productWidthDataRef,
          getSizesData: getSizesWidthData,
          setData: setProductWidthData,
          setDisableInput: setDisableProductWidthInput,
          fieldName: `priceQuotes.${index}.product_width`,
          fieldNameDisabled: `priceQuotes.${index}.isProductWidthDisabled`,
          formatFn: formatSizeToFiveDecimalsIfMethodIsH2ToH5,
          reEstimateValue: reEstimateWidth,
        });
      }
      //set value and options length
      if (sizeFieldsCount === 2 && valueProductWidth) {
        handleDimensionOptions({
          shouldDisplayFraction,
          dataRef: productLengthDataRef,
          getSizesData: getSizesLengthData,
          setData: setProductLengthData,
          setDisableInput: setDisableProductLengthInput,
          fieldName: `priceQuotes.${index}.product_length`,
          fieldNameDisabled: `priceQuotes.${index}.isProductLengthDisabled`,
          formatFn:
            sizeInputNum === 3
              ? roundSizeForMachiningMethodFromH2ToH5
              : formatSizeToFiveDecimalsIfMethodIsH2ToH5,
          reEstimateValue: reEstimateLength,
        });
      }
      //set value and options dimension 4
      if (sizeFieldsCount === 3 && valueProductLength) {
        handleDimensionOptions({
          shouldDisplayFraction,
          dataRef: dimension4DataRef,
          getSizesData: getSizesDimension4Data,
          setData: setDimension4Data,
          setDisableInput: setDisableDimension4Input,
          fieldName: `priceQuotes.${index}.dimension_4`,
          fieldNameDisabled: `priceQuotes.${index}.isDimension4Disabled`,
          formatFn:
            sizeInputNum === 4
              ? roundSizeForMachiningMethodFromH2ToH5
              : formatSizeToFiveDecimalsIfMethodIsH2ToH5,
          reEstimateValue: reEstimateDimension4,
        });
      }
      //set value and options dimension 5
      if (sizeFieldsCount === 4 && valueDimension4) {
        handleDimensionOptions({
          shouldDisplayFraction,
          dataRef: dimension5DataRef,
          getSizesData: getSizesDimension5Data,
          setData: setDimension5Data,
          setDisableInput: setDisableDimension5Input,
          fieldName: `priceQuotes.${index}.dimension_5`,
          fieldNameDisabled: `priceQuotes.${index}.isDimension5Disabled`,
          formatFn:
            sizeInputNum === 5
              ? roundSizeForMachiningMethodFromH2ToH5
              : formatSizeToFiveDecimalsIfMethodIsH2ToH5,
          reEstimateValue: reEstimateDimension5,
        });
      }
      //set value and options dimension 6
      if (sizeFieldsCount === 5 && valueDimension5) {
        handleDimensionOptions({
          shouldDisplayFraction,
          dataRef: dimension6DataRef,
          getSizesData: getSizesDimension6Data,
          setData: setDimension6Data,
          setDisableInput: setDisableDimension6Input,
          fieldName: `priceQuotes.${index}.dimension_6`,
          fieldNameDisabled: `priceQuotes.${index}.isDimension6Disabled`,
          formatFn: roundSizeForMachiningMethodFromH2ToH5,
          reEstimateValue: reEstimateDimension5,
        });
      }
    }
  };

  const handleSuccessImport = (
    res: AxiosResponse<Response.EstimateManufacturingNextSizesResponse, any>,
    args: Payload.ManufacturingImport,
  ) => {
    if (res?.data?.item?.nextSizes) {
      // Split the sizes by comma, so each field can be identified by its index
      const sizeFields: string[] = args?.currentSizes?.split(',') ?? [];
      const sizeFieldsCount = sizeFields.length;

      // Determine whether to display the fractional size input
      let shouldDisplayFractionSize = false;
      let typeNameInput: TypeNameInput = TYPE_NAME_INPUT.PRODUCT_THICKNESS;
      if (sizeFieldsCount === 1) {
        shouldDisplayFractionSize =
          decimalInputSizeFlags?.isSnpo2BunsuHyoji as boolean;
        typeNameInput = TYPE_NAME_INPUT.PRODUCT_WIDTH;
      }
      if (sizeFieldsCount === 2) {
        shouldDisplayFractionSize =
          decimalInputSizeFlags?.isSnpo3BunsuHyoji as boolean;
        typeNameInput = TYPE_NAME_INPUT.PRODUCT_LENGTH;
      }
      if (sizeFieldsCount === 3) {
        shouldDisplayFractionSize =
          decimalInputSizeFlags?.isSnpo4BunsuHyoji as boolean;
        typeNameInput = TYPE_NAME_INPUT.DIMENSION_4;
      }
      if (sizeFieldsCount === 4) {
        shouldDisplayFractionSize =
          decimalInputSizeFlags?.isSnpo5BunsuHyoji as boolean;
        typeNameInput = TYPE_NAME_INPUT.DIMENSION_5;
      }
      if (sizeFieldsCount === 5) {
        shouldDisplayFractionSize =
          decimalInputSizeFlags?.isSnpo6BunsuHyoji as boolean;
        typeNameInput = TYPE_NAME_INPUT.DIMENSION_6;
      }

      /**
       * Determine if the product meets the following conditions:
       *   - It is a product of both domestic and imported types (combined).
       *   - It is considered an imported product based on the selected thickness size.
       *   - The size should be displayed in fractional format.
       */
      const isImportedProductWithFractionSize =
        isBothTypes && shouldDisplayFractionSize;
      if (
        !(
          !machiningMethodOfZ0AndH2ToH5.includes(
            watchedMachiningItem?.value || '',
          ) &&
          isBothTypes &&
          shapeCode !== SHAPE.PIPE
        ) ||
        isImportedProductWithFractionSize
      ) {
        // Clean and format the incoming array from API
        const uniqueSizes = getUniqueSortedItemsByConvertedValue(
          res?.data?.item?.nextSizes,
        );
        const datas = getSizesWithOriginalValue(uniqueSizes, false);

        if (sizeFieldsCount === 1 && datas?.length > 0) {
          setProductWidthData(datas);
          productWidthDataRef.current = datas;
        }
        if (sizeFieldsCount === 2 && datas?.length > 0) {
          setProductLengthData(datas);
          productLengthDataRef.current = datas;
        }
        if (sizeFieldsCount === 3 && datas?.length > 0) {
          setDimension4Data(datas);
          dimension4DataRef.current = datas;
        }
        if (sizeFieldsCount === 4 && datas?.length > 0) {
          setDimension5Data(datas);
          dimension5DataRef.current = datas;
        }
        if (sizeFieldsCount === 5 && datas?.length > 0) {
          setDimension6Data(datas);
          dimension6DataRef.current = datas;
        }
      }
      //if both types => call api for domestic product
      isBothTypes && handleGetManufacturingSizeDomesticMutation(typeNameInput);
    }
  };

  const handleSuccessTolerance = (
    res: AxiosResponse<Response.Tolerance, any>,
  ) => {
    const data = res?.data?.item;
    handleSetToleranceOptions(data);
  };

  const handleSetToleranceOptions = (data) => {
    const toleranceThicknessOptions = renderToleranceOptions(data?.thickness);
    const toleranceWidthOptions = renderToleranceOptions(data?.width);
    const toleranceLengthOptions = renderToleranceOptions(data?.length);
    const toleranceOutsideDiaOptions = renderToleranceOptions(data?.outsideDia);
    setValue(`priceQuotes.${index}.toleranceOptions`, {
      thickness: toleranceThicknessOptions,
      width: toleranceWidthOptions,
      length: toleranceLengthOptions,
      outsideDia: toleranceOutsideDiaOptions,
      apiCalled: true,
    });
  };

  const getOptionsByFormattedParams = (formattedParams, index) => {
    return [
      {
        label: formattedParams.valueParts[index],
        value: formattedParams.valueParts[index],
        originalValue: formattedParams.valueParts[index],
      },
    ];
  };

  // Main function: process and update re-estimated sizes
  const processReEstimateSizes = (
    sizeRes,
    machiningParam: string,
    convertedSizes?: string,
    originalValue?: string,
    isSwappedS2AndS3?: boolean,
  ) => {
    const reEstimateSizeData = sizeRes;
    if (!reEstimateSizeData) return;

    const bunsuFlags = getSnpoBunsuHyojiFlags(
      machiningParam === MACHINING_METHOD.KP
        ? null
        : reEstimateSizeData?.snpoBunsuHyojis,
      reEstimateSizeData?.snpoSize,
    );

    setDecimalInputSizeFlags(bunsuFlags);
    setValue(`priceQuotes.${index}.decimalInputSizeFlags`, bunsuFlags);

    const normalizedSizesInfo = (reEstimateSizeData?.sizesInfo || []).map(
      (item) => ({
        ...item,
        convertedValue: item.sizeS1,
      }),
    );

    let sizesList = getSizesWithOriginalValue(
      getUniqueSortedItemsByConvertedValue(normalizedSizesInfo),
      true,
    ) as any;

    if (MACHINES_TO_SORT_SIZE.includes(machiningParam)) {
      sizesList = sortSizesByValue(sizesList) as {
        label: string;
        value: string;
        key: string;
        originalValue: string;
        processingRange?: Response.ProcessingRange | null;
      }[];
    }

    setManufacturingSizes({
      ...reEstimateSizeData,
      sizes: sizesList,
    });

    const unitSizes = reEstimateSizeData?.unitSizes;
    const snpoSize = Number(reEstimateSizeData?.snpoSize) || 0;

    const joinUnit = Array.isArray(unitSizes)
      ? unitSizes
          .slice(0, snpoSize)
          .filter((item) => item !== '')
          .join(' x ')
      : '';

    setLabelUnitSize1(joinUnit);

    const formattedParams = processFormatAndValues(
      reEstimateSizeData?.snpoGuide,
      convertedSizes || '',
      originalValue || '',
      reEstimateSizeData?.snpoSize,
    );

    if (convertedSizes) {
      const productWidthOptions = getOptionsByFormattedParams(
        formattedParams,
        1,
      );
      const productLengthOptions = getOptionsByFormattedParams(
        formattedParams,
        2,
      );
      const dimension4Options = getOptionsByFormattedParams(formattedParams, 3);
      const dimension5Options = getOptionsByFormattedParams(formattedParams, 4);
      const dimension6Options = getOptionsByFormattedParams(formattedParams, 5);

      const isClearProductWidth =
        isSwappedS2AndS3 && formattedParams?.sizeInputNum === 3;
      const isClearProductLength =
        isSwappedS2AndS3 &&
        (formattedParams?.sizeInputNum === 3 ||
          formattedParams?.sizeInputNum === 4);
      const isClearDimension4 =
        isSwappedS2AndS3 &&
        (formattedParams?.sizeInputNum === 4 ||
          formattedParams?.sizeInputNum === 5);
      const isClearDimension5 =
        isSwappedS2AndS3 &&
        (formattedParams?.sizeInputNum === 5 ||
          formattedParams?.sizeInputNum === 6);
      const isClearDimension6 =
        isSwappedS2AndS3 && formattedParams?.sizeInputNum === 6;

      !isClearProductWidth && setProductWidthData(productWidthOptions);
      !isClearProductLength && setProductLengthData(productLengthOptions);
      !isClearDimension4 && setDimension4Data(dimension4Options);
      !isClearDimension5 && setDimension5Data(dimension5Options);
      !isClearDimension6 && setDimension6Data(dimension6Options);

      setValue(
        `priceQuotes.${index}.product_thickness`,
        formattedParams.valueParts[0],
      );
      setValue(
        `priceQuotes.${index}.product_width`,
        !isClearProductWidth ? formattedParams.valueParts[1] : '',
      );
      setValue(
        `priceQuotes.${index}.product_length`,
        !isClearProductLength ? formattedParams.valueParts[2] : '',
      );
      setValue(
        `priceQuotes.${index}.dimension_4`,
        !isClearDimension4 ? formattedParams.valueParts[3] : '',
      );
      setValue(
        `priceQuotes.${index}.dimension_5`,
        !isClearDimension5 ? formattedParams.valueParts[4] : '',
      );
      setValue(
        `priceQuotes.${index}.dimension_6`,
        !isClearDimension6 ? formattedParams.valueParts[5] : '',
      );

      if (
        formattedParams.originalValueParts[0] !== formattedParams.valueParts[0]
      ) {
        setConversionSize1(formattedParams.originalValueParts[0]);
        setValue(
          `priceQuotes.${index}.converted_product_thickness`,
          formattedParams.originalValueParts[0],
        );
      }
      if (
        !isClearProductWidth &&
        formattedParams.originalValueParts[1] !== formattedParams.valueParts[1]
      ) {
        setConversionSize2(formattedParams.originalValueParts[1]);
        setValue(
          `priceQuotes.${index}.converted_product_width`,
          formattedParams.originalValueParts[1],
        );
      }
      if (
        !isClearProductLength &&
        formattedParams.originalValueParts[2] !== formattedParams.valueParts[2]
      ) {
        setConversionSize3(formattedParams.originalValueParts[2]);
        setValue(
          `priceQuotes.${index}.converted_product_length`,
          formattedParams.originalValueParts[2],
        );
      }
      if (
        !isClearDimension4 &&
        formattedParams.originalValueParts[3] !== formattedParams.valueParts[3]
      ) {
        setConversionSize4(formattedParams.originalValueParts[3]);
        setValue(
          `priceQuotes.${index}.converted_dimension_4`,
          formattedParams.originalValueParts[3],
        );
      }
      if (
        !isClearDimension5 &&
        formattedParams.originalValueParts[4] !== formattedParams.valueParts[4]
      ) {
        setConversionSize5(formattedParams.originalValueParts[4]);
        setValue(
          `priceQuotes.${index}.converted_dimension_5`,
          formattedParams.originalValueParts[4],
        );
      }
      if (
        !isClearDimension6 &&
        formattedParams.originalValueParts[5] !== formattedParams.valueParts[5]
      ) {
        setConversionSize6(formattedParams.originalValueParts[5]);
        setValue(
          `priceQuotes.${index}.converted_dimension_6`,
          formattedParams.originalValueParts[5],
        );
      }
    }

    formattedParams?.sizeInputNum < 2 && clearProductWidthValue();
    formattedParams?.sizeInputNum < 3 && clearProductLengthValue();
    formattedParams?.sizeInputNum < 4 && clearDimension4Value();
    formattedParams?.sizeInputNum < 5 && clearDimension5Value();
    formattedParams?.sizeInputNum < 6 && clearDimension6Value();

    setSizeInputNum(formattedParams?.sizeInputNum);
    setValue(
      `priceQuotes.${index}.numberOfInputDimensions`,
      formattedParams?.sizeInputNum?.toString(),
    );
    setValue(`priceQuotes.${index}.sizeItem`, {
      ...reEstimateSizeData,
      sizes: sizesList,
    });
    setStockError('');
  };

  const clearProductWidthValue = () => {
    setConversionSize2(undefined);
    setProductWidthData(undefined);
    setValue(`priceQuotes.${index}.product_width`, '');
    setValue(`priceQuotes.${index}.converted_product_width`, '');
    clearErrors(`priceQuotes.${index}.product_width`);
  };

  const clearProductLengthValue = () => {
    setConversionSize3(undefined);
    setProductLengthData(undefined);
    setValue(`priceQuotes.${index}.product_length`, '');
    setValue(`priceQuotes.${index}.converted_product_length`, '');
    clearErrors(`priceQuotes.${index}.product_length`);
  };

  const clearDimension4Value = () => {
    setConversionSize4(undefined);
    setDimension4Data(undefined);
    setValue(`priceQuotes.${index}.dimension_4`, '');
    setValue(`priceQuotes.${index}.converted_dimension_4`, '');
    clearErrors(`priceQuotes.${index}.dimension_4`);
  };

  const clearDimension5Value = () => {
    setConversionSize5(undefined);
    setDimension5Data(undefined);
    setValue(`priceQuotes.${index}.dimension_5`, '');
    setValue(`priceQuotes.${index}.converted_dimension_5`, '');
    clearErrors(`priceQuotes.${index}.dimension_5`);
  };

  const clearDimension6Value = () => {
    setConversionSize6(undefined);
    setDimension6Data(undefined);
    setValue(`priceQuotes.${index}.dimension_6`, '');
    setValue(`priceQuotes.${index}.converted_dimension_6`, '');
    clearErrors(`priceQuotes.${index}.dimension_6`);
  };

  const { isFetching } = useMultipleManufacturingSize(
    dataReEstimate?.id,
    productCode,
    watchedMachiningItem?.value,
    setEstimateErrors,
    isProductFinished,
    processReEstimateSizes,
    {
      enabled: shouldFetchManufacturingSize,
    },
  );

  const { getManufacturingNextSizesZ0 } = useManufacturingNextSizesZ0({
    handleSuccess: handleSuccessDomestic,
  });
  const { getManufacturingNextSizes } = useManufacturingNextSizes({
    handleSuccess: handleSuccessImport,
  });

  const { getToleranceMutaion } = useTolerance({
    handleSuccess: handleSuccessTolerance,
  });

  const handleGetToleranceMutaion = () => {
    if (productCode) {
      const currentProductSplit = productFormat.splitProductId(productCode);
      getToleranceMutaion.mutate({
        shhnCd: currentProductSplit?.shhnCd || undefined,
        hakudoShhnCd: currentProductSplit?.hakudoShhnCd || undefined,
        api1ShhnCd: currentProductSplit?.api1ShhnCd || undefined,
        api2ShhnCd: currentProductSplit?.api2ShhnCd || undefined,
        kakoCd: watchedMachiningItem?.value,
        finishingSize:
          getValues(`priceQuotes.${index}.converted_product_thickness`) ||
          getValues(`priceQuotes.${index}.product_thickness`),
      });
    }
  };

  useEffect(() => {
    if (
      isResetByMachiningMethod &&
      isShowMachiningTolerance &&
      (!getValues(`priceQuotes.${index}.machining_thickness`) ||
        !getValues(`priceQuotes.${index}.machining_width`) ||
        !getValues(`priceQuotes.${index}.machining_length`) ||
        !getValues(`priceQuotes.${index}.machining_outside_diameter`) ||
        !getValues(`priceQuotes.${index}.machining_inside_diameter`))
    ) {
      handleGetToleranceMutaion();
    }
  }, [
    isShowMachiningTolerance,
    watchedMachiningItem?.value,
    getValues(`priceQuotes.${index}.machining_thickness`),
    getValues(`priceQuotes.${index}.machining_width`),
    getValues(`priceQuotes.${index}.machining_length`),
    getValues(`priceQuotes.${index}.machining_outside_diameter`),
    getValues(`priceQuotes.${index}.machining_inside_diameter`),
  ]);

  useEffect(() => {
    if (
      dataReEstimate?.product &&
      isDomesticProduct(dataReEstimate?.product) &&
      isImportedProduct(dataReEstimate?.product)
    ) {
      setIsBothTypes(true);
    } else {
      setIsBothTypes(false);
    }
  }, [dataReEstimate?.product]);

  const productSplit = productFormat.splitProductId(dataReEstimate?.product);

  useEffect(() => {
    if (dataReEstimate?.toleranceRes) {
      handleSetToleranceOptions(dataReEstimate?.toleranceRes);
    }
  }, [dataReEstimate?.toleranceRes]);

  useEffect(() => {
    // auto fill value from estimate data from Order history page or product info from Product Detail Page
    processReEstimateSizes(
      dataReEstimate?.sizeRes,
      dataReEstimate?.setdnKakoCd,
      dataReEstimate?.convertedSizes,
      dataReEstimate?.shiagsnpo,
      dataReEstimate?.tsuikaJoho?.isSwappedS2AndS3,
    );
  }, [dataReEstimate?.sizeRes]);

  useEffect(() => {
    // auto fill value from estimate data from Order history page for productFinished
    if (isProductFinished && dataReEstimate?.seihnJoho) {
      setValue(`priceQuotes.${index}.seihnJoho`, dataReEstimate?.seihnJoho);
    }
  }, [isProductFinished, dataReEstimate?.seihnJoho]);

  // Swap options when quoting
  useEffect(() => {
    if (isSwapDimension) {
      if (sizeInputNum.toString() === NUMBER_OF_INPUT_DIMENSIONS.QUANTITY_3) {
        setProductWidthData(productLengthData);
        setProductLengthData(productWidthData);
      }
      if (sizeInputNum.toString() === NUMBER_OF_INPUT_DIMENSIONS.QUANTITY_4) {
        setProductLengthData(dimension4Data);
        setDimension4Data(productWidthData);
      }
      if (sizeInputNum.toString() === NUMBER_OF_INPUT_DIMENSIONS.QUANTITY_5) {
        setDimension4Data(dimension5Data);
        setDimension5Data(dimension4Data);
      }
      if (sizeInputNum.toString() === NUMBER_OF_INPUT_DIMENSIONS.QUANTITY_6) {
        setDimension5Data(dimension6Data);
        setDimension6Data(dimension5Data);
      }
    }
  }, [isSwapDimension]);

  const handleGetManufacturingSizeDomesticMutation = (
    typeInput: TypeNameInput,
  ) => {
    const isGetSize2 = [
      'product_length',
      'dimension_4',
      'dimension_5',
      'dimension_6',
    ].includes(typeInput);
    const isGetSize3 = ['dimension_4', 'dimension_5', 'dimension_6'].includes(
      typeInput,
    );
    const isGetSize4 = ['dimension_5', 'dimension_6'].includes(typeInput);
    const isGetSize5 = ['dimension_6'].includes(typeInput);

    const sizes1 = getValues(`priceQuotes.${index}.product_thickness`);
    const sizes2 =
      isGetSize2 && getValues(`priceQuotes.${index}.product_width`)
        ? getValues(`priceQuotes.${index}.product_width`)
        : undefined;
    const sizes3 =
      isGetSize3 && getValues(`priceQuotes.${index}.product_length`)
        ? getValues(`priceQuotes.${index}.product_length`)
        : undefined;
    const sizes4 =
      isGetSize4 && getValues(`priceQuotes.${index}.dimension_4`)
        ? getValues(`priceQuotes.${index}.dimension_4`)
        : undefined;
    const sizes5 =
      isGetSize5 && getValues(`priceQuotes.${index}.dimension_5`)
        ? getValues(`priceQuotes.${index}.dimension_5`)
        : undefined;

    const allSizes: (string | undefined)[] = [
      sizes1,
      sizes2,
      sizes3,
      sizes4,
      sizes5,
    ];
    const validSizes = allSizes.filter(Boolean);

    getManufacturingNextSizesZ0.mutate({
      sizes1,
      sizes2,
      lenghtSize1: manufacturingSizes?.snpoSize || '',
      shhnCd: productSplit?.shhnCd || '',
      kakoCd: watchedMachiningItem?.value || '',
      currentSizes: validSizes.join(', '),
    });
  };

  const handleGetManufacturingImportMutation = (typeInput: TypeNameInput) => {
    const isGetSize2 = [
      'product_length',
      'dimension_4',
      'dimension_5',
      'dimension_6',
    ].includes(typeInput);
    const isGetSize3 = ['dimension_4', 'dimension_5', 'dimension_6'].includes(
      typeInput,
    );
    const isGetSize4 = ['dimension_5', 'dimension_6'].includes(typeInput);
    const isGetSize5 = ['dimension_6'].includes(typeInput);

    !isGetSize2 && setConversionSize2(undefined);
    !isGetSize3 && setConversionSize3(undefined);
    !isGetSize4 && setConversionSize4(undefined);
    !isGetSize5 && setConversionSize5(undefined);
    setConversionSize6(undefined);

    const valueProductThickness = getValues(
      `priceQuotes.${index}.product_thickness`,
    );
    const matchedProductThickness = manufacturingSizeOptions?.find(
      (item) => item.value === valueProductThickness,
    );

    const valueProductWidth = getValues(`priceQuotes.${index}.product_width`);
    const matchedProductWidth = productWidthData?.find(
      (item) => item.value === valueProductWidth,
    );

    const valueProductLength = getValues(`priceQuotes.${index}.product_length`);
    const matchedproductLength = productLengthData?.find(
      (item) => item.value === valueProductLength,
    );

    const valueDimension4 = getValues(`priceQuotes.${index}.dimension_4`);
    const matchedDimension4 = dimension4Data?.find(
      (item) => item.value === valueDimension4,
    );

    const valueDimension5 = getValues(`priceQuotes.${index}.dimension_5`);
    const matchedDimension5 = dimension5Data?.find(
      (item) => item.value === valueDimension5,
    );

    const sizes1 =
      matchedProductThickness?.originalValue || valueProductThickness;
    const sizes2 =
      isGetSize2 && (matchedProductWidth?.originalValue || valueProductWidth);
    const sizes3 =
      isGetSize3 && (matchedproductLength?.originalValue || valueProductLength);
    const sizes4 =
      isGetSize4 && (matchedDimension4?.originalValue || valueDimension4);
    const sizes5 =
      isGetSize5 && (matchedDimension5?.originalValue || valueDimension5);

    const allSizes: (string | undefined)[] = [
      sizes1,
      sizes2,
      sizes3,
      sizes4,
      sizes5,
    ];
    const validSizes = allSizes.filter(Boolean);

    getManufacturingNextSizes.mutate({
      api1ShhnCd: productSplit?.api1ShhnCd || undefined,
      api2ShhnCd: productSplit?.api2ShhnCd || undefined,
      hakudoShhnCd: productSplit?.hakudoShhnCd || '',
      sizes1: sizes1 || '',
      sizes2: sizes2 || '',
      lenghtSize1: manufacturingSizes?.snpoSize || '',
      currentSizes: validSizes.join(', '),
    });
  };

  const {
    handleConvertSize,
    conversionSize1,
    conversionSize2,
    conversionSize3,
    conversionSize4,
    conversionSize5,
    conversionSize6,
    setConversionSize1,
    setConversionSize2,
    setConversionSize3,
    setConversionSize4,
    setConversionSize5,
    setConversionSize6,
    conversionSize1MultipleRef,
  } = useConvertSize({
    index,
    getValues: getValues,
    manufacturingSizes: manufacturingSizes,
    product: productCode as string,
    setValue: setValue,
    estimatePriceData: dataReEstimate,
    isFetching: isFetching,
  });

  // Fields for getShowProcessingRange (for current price quote line)
  const valueProductThickness = watch(`priceQuotes.${index}.product_thickness`);
  const valueProductWidth = watch(`priceQuotes.${index}.product_width`);
  const valueProductLength = watch(`priceQuotes.${index}.product_length`);
  const valueDimension4 = watch(`priceQuotes.${index}.dimension_4`);
  const valueDimension5 = watch(`priceQuotes.${index}.dimension_5`);
  const valueDimension6 = watch(`priceQuotes.${index}.dimension_6`);
  // Safe fallback for manufacturingSizes
  const processingRanges = (manufacturingSizes?.sizes as any) ?? [];

  const showProcessingRange = useMemo(() => {
    const getRawValue = (
      convertedValue,
      fallbackConvertedValue: string,
      options: { originalValue: string; convertedValue: string }[],
    ): string => {
      if (convertedValue) return convertedValue;

      const match = options?.find(
        (item) => item?.convertedValue === fallbackConvertedValue,
      );

      return match?.originalValue ?? fallbackConvertedValue;
    };

    const rawWidth = getRawValue(
      conversionSize2,
      valueProductWidth,
      productWidthData,
    );

    const rawLength = getRawValue(
      conversionSize3,
      valueProductLength,
      productLengthData,
    );

    const rawDimension4 = getRawValue(
      conversionSize4,
      valueDimension4,
      dimension4Data,
    );

    const rawDimension5 = getRawValue(
      conversionSize5,
      valueDimension5,
      dimension5Data,
    );

    return getShowProcessingRange({
      valueProductThickness,
      valueProductWidth: rawWidth,
      valueProductLength: rawLength,
      valueDimension4: rawDimension4,
      valueDimension5: rawDimension5,
      processingRanges,
      machiningMethod: watchedMachiningItem?.value || '',
    });
  }, [
    valueProductThickness,
    valueProductWidth,
    valueProductLength,
    valueDimension4,
    valueDimension5,
    processingRanges,
    watchedMachiningItem,
    conversionSize2,
    conversionSize3,
    conversionSize4,
    conversionSize5,
    productWidthData,
    productLengthData,
    dimension4Data,
    dimension5Data,
  ]);

  const isFlatPipeProduct = () => {
    return (
      manufacturingSizes?.keijoInfo?.keijoL1Cd ===
        FLAT_PIPE_PRODUCT.KEIJOL1CD &&
      manufacturingSizes?.keijoInfo?.keijoL2Cd ===
        FLAT_PIPE_PRODUCT.KEIJOL2CD &&
      manufacturingSizes?.keijoInfo?.keijoL3Cd === FLAT_PIPE_PRODUCT.KEIJOL3CD
    );
  };

  const handleCallApiSize = (typeInput: TypeNameInput) => {
    if (isImportedProduct(dataReEstimate?.product)) {
      // if imported product or both types
      handleGetManufacturingImportMutation(typeInput);
    } else if (isDomesticProduct(dataReEstimate?.product)) {
      const shouldDisplayFraction = !typeInput
        ? decimalInputSizeFlags?.isSnpo3BunsuHyoji
        : decimalInputSizeFlags?.isSnpo2BunsuHyoji;

      // if domestic product
      if (
        machiningMethodOfZ0AndH2ToH5.includes(
          watchedMachiningItem?.value || '',
        ) ||
        shouldDisplayFraction
      ) {
        handleGetManufacturingSizeDomesticMutation(typeInput);
      } else {
        if (shapeCode === SHAPE.PIPE) {
          handleGetManufacturingSizeDomesticMutation(typeInput);
        }
      }
    }
  };

  useEffect(() => {
    if (showProcessingRange?.length > 0) {
      setValue(`priceQuotes.${index}.showProcessingRange`, showProcessingRange);
    }
  }, [showProcessingRange]);

  useEffect(() => {
    //if reEstimate => set value for product_width and product_length
    if (
      getValues(`priceQuotes.${index}.product_thickness`) &&
      !getValues(`priceQuotes.${index}.product_width`) &&
      reEstimateWidth
    ) {
      setReEstimateWidth('');
      handleConvertSize('product_width');
    }
    if (
      getValues(`priceQuotes.${index}.product_width`) &&
      !getValues(`priceQuotes.${index}.product_length`) &&
      reEstimateLength
    ) {
      setReEstimateLength('');
      handleConvertSize('product_length');
    }
    if (
      getValues(`priceQuotes.${index}.product_width`) &&
      getValues(`priceQuotes.${index}.product_length`) &&
      !getValues(`priceQuotes.${index}.dimension_4`) &&
      reEstimateDimension4
    ) {
      setValue(`priceQuotes.${index}.dimension_4`, reEstimateDimension4);
      setReEstimateLength('');
      setReEstimateDimension4('');
      handleConvertSize('dimension_4');
    }
    if (
      !getValues(`priceQuotes.${index}.dimension_5`) &&
      reEstimateDimension5
    ) {
      setValue(`priceQuotes.${index}.dimension_5`, reEstimateDimension5);
      setReEstimateDimension5('');
      handleConvertSize('dimension_5');
    }
    if (
      !getValues(`priceQuotes.${index}.dimension_6`) &&
      reEstimateDimension6
    ) {
      setValue(`priceQuotes.${index}.dimension_6`, reEstimateDimension6);
      setReEstimateDimension6('');
      handleConvertSize('dimension_6');
    }
  }, [
    getValues(`priceQuotes.${index}.product_thickness`),
    getValues(`priceQuotes.${index}.product_width`),
    reEstimateWidth,
    reEstimateLength,
    reEstimateDimension4,
    reEstimateDimension5,
    reEstimateDimension6,
  ]);

  useEffect(() => {
    //If it is an internal product and belongs to PPGC types Z0, H2~H5, then the values in the size input fields will be automatically populated.
    const isDomesticProductZ0H2H5 =
      isDomesticProduct(dataReEstimate?.product) &&
      (watchedMachiningItem?.value === MACHINING_METHOD.Z0 ||
        (MACHINING_METHOD.H2TOH5.includes(watchedMachiningItem?.value || '') &&
          checkHasDomesticData(manufacturingSizes?.sizes)));

    if (
      disableProductWidthInput &&
      isDomesticProductZ0H2H5 &&
      valueProductThickness &&
      inputAutoFillByType === TYPE_NAME_INPUT.PRODUCT_THICKNESS
    ) {
      handleCallApiSize(TYPE_NAME_INPUT.PRODUCT_WIDTH);
      setValue(
        `priceQuotes.${index}.inputAutoFillByType`,
        TYPE_NAME_INPUT.PRODUCT_WIDTH,
      );
    }
    if (
      disableProductLengthInput &&
      isDomesticProductZ0H2H5 &&
      valueProductWidth &&
      inputAutoFillByType === TYPE_NAME_INPUT.PRODUCT_WIDTH
    ) {
      handleCallApiSize(TYPE_NAME_INPUT.PRODUCT_LENGTH);
      setValue(
        `priceQuotes.${index}.inputAutoFillByType`,
        TYPE_NAME_INPUT.PRODUCT_LENGTH,
      );
    }
    if (
      disableDimension4Input &&
      isDomesticProductZ0H2H5 &&
      valueProductWidth &&
      valueProductLength &&
      inputAutoFillByType === TYPE_NAME_INPUT.PRODUCT_LENGTH
    ) {
      handleCallApiSize(TYPE_NAME_INPUT.DIMENSION_4);
      setValue(
        `priceQuotes.${index}.inputAutoFillByType`,
        TYPE_NAME_INPUT.DIMENSION_4,
      );
    }
    if (
      disableDimension5Input &&
      isDomesticProductZ0H2H5 &&
      valueProductWidth &&
      valueProductLength &&
      valueDimension4 &&
      inputAutoFillByType === TYPE_NAME_INPUT.DIMENSION_4
    ) {
      handleCallApiSize(TYPE_NAME_INPUT.DIMENSION_5);
      setValue(
        `priceQuotes.${index}.inputAutoFillByType`,
        TYPE_NAME_INPUT.DIMENSION_5,
      );
    }
    if (
      disableDimension6Input &&
      isDomesticProductZ0H2H5 &&
      valueProductWidth &&
      valueProductLength &&
      valueDimension4 &&
      valueDimension5 &&
      inputAutoFillByType === TYPE_NAME_INPUT.DIMENSION_5
    ) {
      handleCallApiSize(TYPE_NAME_INPUT.DIMENSION_6);
    }
  }, [
    inputAutoFillByType,
    disableProductWidthInput,
    disableProductLengthInput,
    disableDimension4Input,
    disableDimension5Input,
    disableDimension6Input,
    valueProductThickness,
    valueProductWidth,
    valueProductLength,
    valueDimension4,
    valueDimension5,
    valueDimension6,
    watchedMachiningItem?.value,
  ]);

  useEffect(() => {
    //disable if product is domestic and machining method is Z0 or (from H2 to H5 and has domestic data)
    const shouldDisable =
      isDomesticProduct(dataReEstimate?.product) &&
      (watchedMachiningItem?.value === MACHINING_METHOD.Z0 ||
        (MACHINING_METHOD.H2TOH5.includes(watchedMachiningItem?.value || '') &&
          checkHasDomesticData(manufacturingSizes?.sizes))) &&
      !isFetching;

    setDisableProductWidthInput(!!shouldDisable);
    setDisableProductLengthInput(!!shouldDisable);

    setValue(`priceQuotes.${index}.isProductWidthDisabled`, !!shouldDisable);
    setValue(`priceQuotes.${index}.isProductLengthDisabled`, !!shouldDisable);
  }, [
    dataReEstimate?.product,
    watchedMachiningItem?.value,
    isFetching,
    isResetByMachiningMethod,
  ]);

  useEffect(() => {
    if (isResetByProductSelection) {
      conversionSize1MultipleRef.current = [];
      setConversionSize1(undefined);
      setConversionSize2(undefined);
      setConversionSize3(undefined);
      setConversionSize4(undefined);
      setConversionSize5(undefined);
      setConversionSize6(undefined);
      setProductWidthData([]);
      setProductLengthData([]);
      !productCode && setSizeInputNum(0);
    }
  }, [isResetByProductSelection]);

  const checkConditionHandleBlurProductThickness = () => {
    // if lenghtSize1 < 6
    if (
      manufacturingSizes?.snpoSize &&
      manufacturingSizes?.snpoSize < 6 &&
      !checkNotGetDataWithImportedProduct()
    ) {
      return true;
    }
    return false;
  };

  const checkNotGetDataWithImportedProduct = () => {
    //if isImportedProduct & machining method = KK,KP,FK,FP => not get data from api
    return (
      isImportedProduct(dataReEstimate?.product) &&
      MACHINING_METHOD.EXCLUSION.includes(watchedMachiningItem?.value || '')
    );
  };

  // Check the label to ensure it contains a fraction
  const isFractionLabel = (label) => {
    if (!label) return false;
    return label.trim().split('/').length > 1;
  };

  const renderConversionSize1 = () => {
    const productThicknessSelected = manufacturingSizeOptions?.find(
      (item) => item?.value === valueProductThickness,
    );

    if (
      decimalInputSizeFlags?.isSnpo1BunsuHyoji &&
      (conversionSize1 || _.size(conversionSize1MultipleRef.current) > 0) &&
      isFractionLabel(productThicknessSelected?.label)
    ) {
      return valueProductThickness;
    }
    if (_.size(conversionSize1MultipleRef.current) > 0) {
      return (
        conversionSize1MultipleRef.current.join('x') +
        manufacturingSizes?.originSize
      );
    } else {
      return (
        conversionSize1 && conversionSize1 + manufacturingSizes?.originSize
      );
    }
  };

  const renderConversionSizeSingle = (conversionSize, inputName) => {
    const productWidthSelected = productWidthDataOptions?.find(
      (item) => item?.value === valueProductWidth,
    );
    const productLengthSelected = productLengthDataOptions?.find(
      (item) => item?.value === valueProductLength,
    );
    const dimension4Selected = dimension4DataOptions?.find(
      (item) => item?.value === valueDimension4,
    );
    const dimension5Selected = dimension5DataOptions?.find(
      (item) => item?.value === valueDimension5,
    );
    const dimension6Selected = dimension6DataOptions?.find(
      (item) => item?.value === valueDimension6,
    );
    if (
      inputName === 'product_width' &&
      decimalInputSizeFlags?.isSnpo2BunsuHyoji &&
      conversionSize &&
      isFractionLabel(productWidthSelected?.label)
    ) {
      return valueProductWidth;
    }
    if (
      inputName === 'product_length' &&
      decimalInputSizeFlags?.isSnpo3BunsuHyoji &&
      conversionSize &&
      isFractionLabel(productLengthSelected?.label)
    ) {
      return valueProductLength;
    }
    if (
      inputName === 'dimension_4' &&
      decimalInputSizeFlags?.isSnpo4BunsuHyoji &&
      conversionSize &&
      isFractionLabel(dimension4Selected?.label)
    ) {
      return valueDimension4;
    }
    if (
      inputName === 'dimension_5' &&
      decimalInputSizeFlags?.isSnpo5BunsuHyoji &&
      conversionSize &&
      isFractionLabel(dimension5Selected?.label)
    ) {
      return valueDimension5;
    }
    if (
      inputName === 'dimension_6' &&
      decimalInputSizeFlags?.isSnpo6BunsuHyoji &&
      conversionSize &&
      isFractionLabel(dimension6Selected?.label)
    ) {
      return valueDimension6;
    }
    return conversionSize && conversionSize + manufacturingSizes?.originSize;
  };

  const resetInputSizeWhenOnBlurSize1 = () => {
    if (checkConditionHandleBlurProductThickness()) {
      setValue(`priceQuotes.${index}.product_width`, '');
      setValue(`priceQuotes.${index}.product_length`, '');
      setValue(`priceQuotes.${index}.dimension_4`, '');
      setValue(`priceQuotes.${index}.dimension_5`, '');
      setValue(`priceQuotes.${index}.dimension_6`, '');
      setValue(`priceQuotes.${index}.converted_product_width`, '');
      setValue(`priceQuotes.${index}.converted_product_length`, '');
      setValue(`priceQuotes.${index}.converted_dimension_4`, '');
      setValue(`priceQuotes.${index}.converted_dimension_5`, '');
      setValue(`priceQuotes.${index}.converted_dimension_6`, '');
      setConversionSize2(undefined);
      setConversionSize3(undefined);
      setConversionSize4(undefined);
      setConversionSize5(undefined);
      setConversionSize6(undefined);
      setIsToggleOnBlurSize1(!isToggleOnBlurSize1);
    }
  };

  const handleBlurSizes1 = () => {
    resetInputSizeWhenOnBlurSize1();
    handleConvertSize('product_thickness');
    setValue(
      `priceQuotes.${index}.inputAutoFillByType`,
      TYPE_NAME_INPUT.PRODUCT_THICKNESS,
    );
    // Use productCode from form to ensure we have the current product value
    handleGetToleranceMutaion();
  };

  const resetInputSizeWhenOnBlur = (typeInput: TypeNameInput) => {
    switch (typeInput) {
      case 'product_width':
        setValue(`priceQuotes.${index}.product_length`, '');
        setValue(`priceQuotes.${index}.dimension_4`, '');
        setValue(`priceQuotes.${index}.dimension_5`, '');
        setValue(`priceQuotes.${index}.dimension_6`, '');
        setValue(`priceQuotes.${index}.converted_product_length`, '');
        setConversionSize3(undefined);
        break;
      case 'product_length':
        setValue(`priceQuotes.${index}.dimension_4`, '');
        setValue(`priceQuotes.${index}.dimension_5`, '');
        setValue(`priceQuotes.${index}.dimension_6`, '');
        break;
      case 'dimension_4':
        setValue(`priceQuotes.${index}.dimension_5`, '');
        setValue(`priceQuotes.${index}.dimension_6`, '');
        break;
      case 'dimension_5':
        setValue(`priceQuotes.${index}.dimension_6`, '');
        break;

      default:
        break;
    }
  };

  const handleBlurInputSizes = (typeInput: TypeNameInput) => {
    if (!checkNotGetDataWithImportedProduct()) {
      resetInputSizeWhenOnBlur(typeInput);
    }
    setValue(`priceQuotes.${index}.inputAutoFillByType`, typeInput);
    handleConvertSize(typeInput);
  };

  const handleFocusInputSizes = (typeInput: TypeNameInput) => {
    if (!checkNotGetDataWithImportedProduct()) {
      handleCallApiSize(typeInput);
    }
  };

  // Handle select focus to enable API call
  const handleFocusProductFinshed = () => {
    if (dataReEstimate?.product) {
      setShouldFetchProductsInfor(true);
    }
  };

  return (
    <Row align="middle" className="manufacturing-size">
      <Tooltip
        title={
          !isProductFinished &&
          sizeInputNum > 0 &&
          `${t('multiple_quotes_page.dimension_element')}${getLabelDimension(
            manufacturingSizes?.snpoGuide || '',
          )}`
        }
        color="#fff"
        overlayInnerStyle={{ color: '#000' }}
      >
        <div className="manufacturing-size__guide-label-wrap">
          <p className="manufacturing-size__guide-label">
            {isProductFinished && `${t('pricing_page.type_selection')}:`}
            {!isProductFinished &&
              sizeInputNum > 0 &&
              `${t(
                'multiple_quotes_page.dimension_element',
              )}${getLabelDimension(manufacturingSizes?.snpoGuide || '')}`}
          </p>
        </div>
      </Tooltip>

      <Col className="manufacturing-size__container">
        <div className="manufacturing-size__right">
          {!isProductFinished && (
            <div className="manufacturing-size__inputs">
              {sizeInputNum >= 1 && (
                <div className="manufacturing-size__input-row">
                  <div className="manufacturing-size__input-group">
                    <Tooltip
                      title={
                        !decimalInputSizeFlags?.isSnpo1BunsuHyoji &&
                        (getValues(`priceQuotes.${index}.product_thickness`)
                          ?.length ?? 0) > 6
                          ? getValues(`priceQuotes.${index}.product_thickness`)
                          : ''
                      }
                      overlayInnerStyle={{
                        backgroundColor: '#fff',
                        color: '#000',
                      }}
                    >
                      <div className="manufacturing-size__input-custom">
                        <ManufacturingSizeInputField<FormValue>
                          showErrorTooltip
                          useAutoSuggest={
                            !decimalInputSizeFlags?.isSnpo1BunsuHyoji
                          }
                          onBlur={handleBlurSizes1}
                          onClear={() => {
                            resetInputSizeWhenOnBlurSize1();
                            setValue(
                              `priceQuotes.${index}.converted_product_thickness`,
                              '',
                            );
                            setConversionSize1(undefined);
                          }}
                          controller={{
                            control,
                            name: `priceQuotes.${index}.product_thickness`,
                          }}
                          options={
                            checkNotGetDataWithImportedProduct()
                              ? undefined
                              : manufacturingSizeOptions
                          }
                          newRegex={REGEX.REGEX_DIMENSIONS_SIZES1}
                          className="manufacturing-size__input"
                          autoCompleteProps={{
                            allowClear: true,
                            maxTagTextLength: 48,
                            filterOption: (inputValue, option) => {
                              if (
                                !getValues(
                                  `priceQuotes.${index}.product_thickness`,
                                )
                              )
                                return true;
                              const label =
                                option?.label?.toString().toUpperCase() || '';
                              return label.includes(inputValue.toUpperCase());
                            },
                          }}
                          selectProps={{
                            allowClear: true,
                          }}
                        />
                      </div>
                    </Tooltip>
                    <Typography.Text className="manufacturing-size__unit-label">
                      <Tooltip
                        title={
                          Number(manufacturingSizes?.snpoSize) > 1
                            ? labelUnitSize1
                            : ''
                        }
                        overlayInnerStyle={{
                          backgroundColor: '#fff',
                          color: '#000',
                        }}
                      >
                        <span
                          className={`manufacturing-size__unit ${
                            Number(manufacturingSizes?.snpoSize) > 1 &&
                            'unit-product-thickness'
                          }`}
                        >
                          {labelUnitSize1}
                        </span>
                      </Tooltip>
                      {sizeInputNum > 1 &&
                        t('pricing_page.product_size_field.thickness_after')}
                    </Typography.Text>
                  </div>
                  <Typography.Text>{renderConversionSize1()}</Typography.Text>
                  <Typography.Text className="processing-range-text">
                    {showProcessingRange[0]}
                  </Typography.Text>
                </div>
              )}
              {sizeInputNum >= 2 && (
                <div className="manufacturing-size__input-row">
                  <div className="manufacturing-size__input-group">
                    <Tooltip
                      title={
                        !decimalInputSizeFlags?.isSnpo2BunsuHyoji &&
                        (getValues(`priceQuotes.${index}.product_width`)
                          ?.length ?? 0) > 6
                          ? getValues(`priceQuotes.${index}.product_width`)
                          : ''
                      }
                      overlayInnerStyle={{
                        backgroundColor: '#fff',
                        color: '#000',
                      }}
                    >
                      <div className="manufacturing-size__input-custom">
                        <ManufacturingSizeInputField<FormValue>
                          showErrorTooltip
                          useAutoSuggest={
                            !decimalInputSizeFlags?.isSnpo2BunsuHyoji
                          }
                          onBlur={() =>
                            handleBlurInputSizes(TYPE_NAME_INPUT.PRODUCT_WIDTH)
                          }
                          onFocus={() =>
                            handleFocusInputSizes(TYPE_NAME_INPUT.PRODUCT_WIDTH)
                          }
                          controller={{
                            control,
                            name: `priceQuotes.${index}.product_width`,
                          }}
                          options={productWidthDataOptions}
                          newRegex={REGEX.REGEX_DIMENSIONS_SIZES2_SIZES3}
                          className="manufacturing-size__input"
                          autoCompleteProps={{
                            allowClear: true,
                            maxTagTextLength: 10,
                            showSearch: true,
                            filterOption: false,
                            showArrow: false,
                            disabled: disableProductWidthInput,
                            dropdownClassName:
                              watchedMachiningItem?.value ===
                                MACHINING_METHOD.KP &&
                              'hiddenAutoCompleteOptions',
                          }}
                          selectProps={{
                            allowClear: true,
                          }}
                        />
                      </div>
                    </Tooltip>

                    <Typography.Text className="manufacturing-size__unit-label">
                      <span className="manufacturing-size__unit">
                        {
                          manufacturingSizes?.unitSizes[
                            manufacturingSizes?.snpoSize
                          ]
                        }
                      </span>
                      {sizeInputNum > 2 &&
                        t('pricing_page.product_size_field.width_after')}
                    </Typography.Text>
                  </div>
                  <div>
                    <Typography.Text>
                      {renderConversionSizeSingle(
                        conversionSize2,
                        'product_width',
                      )}
                    </Typography.Text>
                    <Typography.Text className="processing-range-text">
                      {showProcessingRange[1]}
                    </Typography.Text>
                  </div>
                </div>
              )}
              {sizeInputNum >= 3 && (
                <div className="manufacturing-size__input-row">
                  <div className="manufacturing-size__input-group">
                    <Tooltip
                      title={
                        !decimalInputSizeFlags?.isSnpo3BunsuHyoji &&
                        (getValues(`priceQuotes.${index}.product_length`)
                          ?.length ?? 0) > 6
                          ? getValues(`priceQuotes.${index}.product_length`)
                          : ''
                      }
                      overlayInnerStyle={{
                        backgroundColor: '#fff',
                        color: '#000',
                      }}
                    >
                      <div className="manufacturing-size__input-custom">
                        <ManufacturingSizeInputField<FormValue>
                          showErrorTooltip
                          useAutoSuggest={
                            !decimalInputSizeFlags?.isSnpo3BunsuHyoji
                          }
                          onBlur={() =>
                            handleBlurInputSizes(TYPE_NAME_INPUT.PRODUCT_LENGTH)
                          }
                          onFocus={() =>
                            handleFocusInputSizes(
                              TYPE_NAME_INPUT.PRODUCT_LENGTH,
                            )
                          }
                          controller={{
                            control,
                            name: `priceQuotes.${index}.product_length`,
                          }}
                          options={productLengthDataOptions}
                          newRegex={REGEX.REGEX_DIMENSIONS_SIZES2_SIZES3}
                          className="manufacturing-size__input"
                          autoCompleteProps={{
                            allowClear: true,
                            maxTagTextLength: 10,
                            showSearch: true,
                            filterOption: false,
                            disabled: disableProductLengthInput,
                            dropdownClassName:
                              watchedMachiningItem?.value ===
                                MACHINING_METHOD.KP &&
                              'hiddenAutoCompleteOptions',
                          }}
                          selectProps={{
                            allowClear: true,
                          }}
                        />
                      </div>
                    </Tooltip>

                    <Typography.Text className="manufacturing-size__unit-label">
                      <span className="manufacturing-size__unit">
                        {
                          manufacturingSizes?.unitSizes[
                            manufacturingSizes?.snpoSize + 1
                          ]
                        }
                      </span>
                      {sizeInputNum > 3 &&
                        t('pricing_page.product_size_field.width_after')}
                    </Typography.Text>
                  </div>
                  <div>
                    <Typography.Text>
                      {renderConversionSizeSingle(
                        conversionSize3,
                        'product_length',
                      )}
                    </Typography.Text>
                    <Typography.Text className="processing-range-text">
                      {showProcessingRange[2]}
                    </Typography.Text>
                  </div>
                </div>
              )}

              {sizeInputNum >= 4 && (
                <div className="manufacturing-size__input-row">
                  <div className="manufacturing-size__input-group dimension-input-group">
                    <div className="manufacturing-size__input-custom">
                      <ManufacturingSizeInputField<FormValue>
                        showErrorTooltip
                        useAutoSuggest={
                          !decimalInputSizeFlags?.isSnpo4BunsuHyoji
                        }
                        onBlur={() =>
                          handleBlurInputSizes(TYPE_NAME_INPUT.DIMENSION_4)
                        }
                        onFocus={() =>
                          handleFocusInputSizes(TYPE_NAME_INPUT.DIMENSION_4)
                        }
                        controller={{
                          control,
                          name: `priceQuotes.${index}.dimension_4`,
                        }}
                        options={dimension4DataOptions}
                        newRegex={REGEX.REGEX_DIMENSIONS_SIZES2_SIZES3}
                        className="manufacturing-size__input"
                        autoCompleteProps={{
                          allowClear: true,
                          maxTagTextLength: 10,
                          showSearch: true,
                          filterOption: false,
                          disabled: disableDimension4Input,
                          dropdownClassName:
                            watchedMachiningItem?.value ===
                              MACHINING_METHOD.KP &&
                            'hiddenAutoCompleteOptions',
                        }}
                        selectProps={{
                          allowClear: true,
                        }}
                      />
                    </div>
                    <Typography.Text className="manufacturing-size__unit-label">
                      <span className="manufacturing-size__unit">
                        {
                          manufacturingSizes?.unitSizes[
                            manufacturingSizes?.snpoSize + 2
                          ]
                        }
                      </span>
                      {sizeInputNum > 4 &&
                        t('pricing_page.product_size_field.width_after')}
                    </Typography.Text>
                  </div>
                  <div>
                    <Typography.Text>
                      {renderConversionSizeSingle(
                        conversionSize4,
                        'dimension_4',
                      )}
                    </Typography.Text>
                    <Typography.Text className="processing-range-text">
                      {showProcessingRange[3]}
                    </Typography.Text>
                  </div>
                </div>
              )}
              {sizeInputNum >= 5 && (
                <div className="manufacturing-size__input-row">
                  <div className="manufacturing-size__input-group dimension-input-group">
                    <div className="manufacturing-size__input-custom">
                      <ManufacturingSizeInputField<FormValue>
                        showErrorTooltip
                        useAutoSuggest={
                          !decimalInputSizeFlags?.isSnpo5BunsuHyoji
                        }
                        onBlur={() =>
                          handleBlurInputSizes(TYPE_NAME_INPUT.DIMENSION_5)
                        }
                        onFocus={() =>
                          handleFocusInputSizes(TYPE_NAME_INPUT.DIMENSION_5)
                        }
                        controller={{
                          control,
                          name: `priceQuotes.${index}.dimension_5`,
                        }}
                        options={dimension5DataOptions}
                        newRegex={REGEX.REGEX_DIMENSIONS_SIZES2_SIZES3}
                        className="manufacturing-size__input"
                        autoCompleteProps={{
                          allowClear: true,
                          maxTagTextLength: 10,
                          showSearch: true,
                          filterOption: false,
                          disabled: disableDimension5Input,
                          dropdownClassName:
                            watchedMachiningItem?.value ===
                              MACHINING_METHOD.KP &&
                            'hiddenAutoCompleteOptions',
                        }}
                        selectProps={{
                          allowClear: true,
                        }}
                      />
                    </div>
                    <Typography.Text className="manufacturing-size__unit-label">
                      <span className="manufacturing-size__unit">
                        {
                          manufacturingSizes?.unitSizes[
                            manufacturingSizes?.snpoSize + 3
                          ]
                        }
                      </span>
                      {sizeInputNum > 5 &&
                        t('pricing_page.product_size_field.width_after')}
                    </Typography.Text>
                  </div>
                  <div>
                    <Typography.Text>
                      {renderConversionSizeSingle(
                        conversionSize5,
                        'dimension_5',
                      )}
                    </Typography.Text>
                    <Typography.Text className="processing-range-text">
                      {showProcessingRange[4]}
                    </Typography.Text>
                  </div>
                </div>
              )}
              {sizeInputNum >= 6 && (
                <div className="manufacturing-size__input-row">
                  <div className="manufacturing-size__input-group dimension-input-group">
                    <div className="manufacturing-size__input-custom">
                      <ManufacturingSizeInputField<FormValue>
                        showErrorTooltip
                        useAutoSuggest={
                          !decimalInputSizeFlags?.isSnpo6BunsuHyoji
                        }
                        onBlur={() =>
                          handleBlurInputSizes(TYPE_NAME_INPUT.DIMENSION_6)
                        }
                        onFocus={() =>
                          handleFocusInputSizes(TYPE_NAME_INPUT.DIMENSION_6)
                        }
                        controller={{
                          control,
                          name: `priceQuotes.${index}.dimension_6`,
                        }}
                        options={dimension6DataOptions}
                        newRegex={REGEX.REGEX_DIMENSIONS_SIZES2_SIZES3}
                        className="manufacturing-size__input"
                        autoCompleteProps={{
                          allowClear: true,
                          maxTagTextLength: 10,
                          showSearch: true,
                          filterOption: false,
                          disabled: disableDimension6Input,
                          dropdownClassName:
                            watchedMachiningItem?.value ===
                              MACHINING_METHOD.KP &&
                            'hiddenAutoCompleteOptions',
                        }}
                        selectProps={{
                          allowClear: true,
                        }}
                      />
                    </div>
                    <Typography.Text className="manufacturing-size__unit-label">
                      <span className="manufacturing-size__unit">
                        {
                          manufacturingSizes?.unitSizes[
                            manufacturingSizes?.snpoSize + 4
                          ]
                        }
                      </span>
                      {sizeInputNum > 6 &&
                        t('pricing_page.product_size_field.width_after')}
                    </Typography.Text>
                  </div>
                  <div>
                    <Typography.Text>
                      {renderConversionSizeSingle(
                        conversionSize6,
                        'dimension_6',
                      )}
                    </Typography.Text>
                    <Typography.Text className="processing-range-text">
                      {showProcessingRange[5]}
                    </Typography.Text>
                  </div>
                </div>
              )}
            </div>
          )}
          {isProductFinished && (
            <SelectField<FormValue>
              showErrorTooltip
              controller={{
                control,
                name: `priceQuotes.${index}.seihnJoho`,
              }}
              onFocus={handleFocusProductFinshed}
              selectProps={{
                placeholder: t('pricing_page.type_selection'),
                allowClear: true,
                options: listOptionProductFinshed,
              }}
              className="manufacturing-size__input select-finished-product"
            />
          )}
        </div>
      </Col>
    </Row>
  );
};

export default ManuFacturingSize;
